import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Button,
  CardNotification,
  Loading,
  OrderDirection,
  Pager,
  Table,
  Text,
  toast,
} from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import {
  OrderByDirection,
  ProgrammingStructure,
  ProgrammingStructureOrderByFields,
} from "@models/ISchema";
import {
  useDeleteProgrammingStructure,
  useGetProgrammingStructures,
  useUpdateProgrammingStructure,
} from "@services/collections/programming-structures/programmingStructuresServices";
import {
  ProgrammingStructuresSearchParams,
  ProgrammingStructuresSort,
} from "@services/types/programming-structures/programmingStructuresFiltersTypes";
import { buildFilterFields, getStructureSourceLabelKey } from "@utils/programmingStructures";
import { getColumnCell, getPillCell } from "@common/utils/table.util";
import { useProgrammingStructuresPermissions } from "@modules/programming-structures/hooks/useProgrammingStructuresPermissions";
import useProgrammingStructuresFilters from "@modules/programming-structures/hooks/useProgrammingStructuresFilters";
import useContextUrl from "@common/hooks/useContextUrl";
import DimensionsSearchSkeleton from "@components/dimensions-search-skeleton";
import ProgrammingStructuresLayout from "@modules/programming-structures/layout/programming-structures-layout";
import ProgrammingStructuresFiltersBar from "@modules/programming-structures/components/programming-structures-filters";
import PublishStructureModal from "@modules/programming-structures/components/publish-structure-modal";
import DeleteStructureModal from "@modules/programming-structures/components/delete-structure-modal";
import VisibilityStructureModal from "@modules/programming-structures/components/visibility-structure-modal";
import ProgrammingStructuresMenu from "@modules/programming-structures/components/programming-structures-menu";
import styles from "./programmingStructures.module.scss";

const rowsPerPage = 10;

const ProgrammingStructures = () => {
  const history = useHistory();
  const location = useLocation();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { scenario, workspace, origin } = useContextUrl();
  const [selectedStructure, setSelectedStructure] = useState<ProgrammingStructure | null>(null);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [isAdvancedPanelOpen, setIsAdvancedPanelOpen] = useState(false);
  const hasRequestedOnMount = useRef(false);

  const {
    form,
    applied,
    initialSort,
    initialPage,
    setSubject,
    setStructureCode,
    toggleSource,
    togglePublished,
    toggleVisible,
    isPending,
    hasAppliedSearch,
    hasSelection,
    primaryActionLabel,
    applyFilters,
    clearFilters,
  } = useProgrammingStructuresFilters({
    initialSearch: location.search,
    isAdvancedPanelOpen,
  });

  const [sort, setSort] = useState<ProgrammingStructuresSort>(initialSort);
  const [currentPage, setCurrentPage] = useState(initialPage);

  const { getProgrammingStructures, loading, programmingStructures, pageInfo } =
    useGetProgrammingStructures();

  const { deleteProgrammingStructure, loading: isDeletingStructure } =
    useDeleteProgrammingStructure();

  const { updateProgrammingStructure, loading: isUpdatingStructure } =
    useUpdateProgrammingStructure();

  const { permissions } = useProgrammingStructuresPermissions();

  /**
   * The single entry point for every request. Always receives the *applied*
   * filters, never the edited form, so sorting, paging and post-mutation
   * refetches cannot leak unapplied changes into the query.
   */
  const fetchStructures = useCallback(
    ({ filters, sort: nextSort, page }: ProgrammingStructuresSearchParams) => {
      setSort(nextSort);
      setCurrentPage(page);

      getProgrammingStructures({
        scenarioId: scenario,
        filter: {
          fields: buildFilterFields(filters),
          orderBy: [nextSort],
          pagination: {
            page,
            size: rowsPerPage,
          },
        },
      });
    },
    [scenario],
  );

  const handleApplyFilters = useCallback(() => {
    fetchStructures({ filters: applyFilters(), sort, page: 1 });
  }, [applyFilters, fetchStructures, sort]);

  const handleClearFilters = useCallback(() => {
    fetchStructures({ filters: clearFilters(), sort, page: 1 });
  }, [clearFilters, fetchStructures, sort]);

  const handleSortChange = useCallback(
    (nextSort: ProgrammingStructuresSort) => {
      fetchStructures({ filters: applied, sort: nextSort, page: currentPage });
    },
    [applied, currentPage, fetchStructures],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      fetchStructures({ filters: applied, sort, page });
    },
    [applied, sort, fetchStructures],
  );

  const refetchCurrentPage = useCallback(() => {
    fetchStructures({ filters: applied, sort, page: currentPage });
  }, [applied, sort, currentPage, fetchStructures]);

  const handleDeleteProgrammingStructure = useCallback(
    async (id: string) => {
      if (isDeletingStructure) return;

      const { data } = await deleteProgrammingStructure({
        scenarioId: scenario,
        originId: origin,
        input: {
          changesets: {
            deletes: [id],
          },
        },
      });

      if (data?.cube?.programmingStructuresCrud?.commited) {
        refetchCurrentPage();
        toast.success(t("programming-structures.notifications.toast.delete-success"), {
          autoClose: 3000,
        });
      }
    },
    [scenario, origin, deleteProgrammingStructure, refetchCurrentPage, language],
  );

  const handleUpdateVisibilityOrPublish = useCallback(
    async (
      id: string,
      isPublished: boolean,
      isVisible: boolean,
      operation: "visibility" | "publish",
    ) => {
      if (isUpdatingStructure) return;

      const { data } = await updateProgrammingStructure({
        scenarioId: scenario,
        originId: origin,
        input: {
          changesets: {
            updates: [
              {
                id,
                isVisible,
                isPublished,
              },
            ],
          },
        },
      });

      if (data?.cube?.programmingStructuresCrud?.commited) {
        const publishMessage = t("programming-structures.notifications.toast.publish-success");
        const visibilityMessage = t(
          "programming-structures.notifications.toast.update-visibility-success",
        );

        toast.success(operation === "publish" ? publishMessage : visibilityMessage, {
          autoClose: 3000,
        });
        refetchCurrentPage();
      }
    },
    [scenario, origin, updateProgrammingStructure, refetchCurrentPage, language],
  );

  /**
   * Exactly one request on mount, built from the state hydrated out of the URL.
   * The query string is an inbound-only channel: once it has seeded the filter
   * state it is stripped, so the address bar stays clean and a later reload
   * starts from an unfiltered listing.
   */
  useEffect(() => {
    if (!scenario || hasRequestedOnMount.current) return;

    hasRequestedOnMount.current = true;
    fetchStructures({ filters: applied, sort: initialSort, page: initialPage });

    if (location.search) history.replace({ pathname: location.pathname, search: "" });
  }, [scenario, fetchStructures]);

  const hasResults = !!programmingStructures?.length;
  const shouldShowPendingChangesWarning = isPending && hasAppliedSearch && !loading;

  return (
    <ProgrammingStructuresLayout>
      {(isDeletingStructure || isUpdatingStructure) && <Loading />}

      {!!permissions.create && (
        <Button
          variant="secondary"
          leftIcon="plus"
          onClick={() =>
            history.push(`/programming-structures/new/${workspace}/${scenario}/${origin}`)
          }
        >
          {t("programming-structures.actions.create-new-structure")}
        </Button>
      )}

      <ProgrammingStructuresFiltersBar
        filters={form}
        scenarioId={scenario}
        originId={origin}
        isAdvancedPanelOpen={isAdvancedPanelOpen}
        isLoading={loading}
        isPending={isPending}
        hasSelection={hasSelection}
        primaryActionLabel={primaryActionLabel}
        onToggleAdvancedPanel={() => setIsAdvancedPanelOpen(current => !current)}
        onSubjectChange={setSubject}
        onStructureCodeChange={setStructureCode}
        onSourceToggle={toggleSource}
        onPublishedToggle={togglePublished}
        onVisibleToggle={toggleVisible}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
      />

      {loading ? (
        <DimensionsSearchSkeleton showHeader={false} loading={loading} rows={rowsPerPage} />
      ) : (
        <section className={styles.programmingStructures}>
          <Table
            data={programmingStructures}
            initialSort={{
              column: sort?.field || ProgrammingStructureOrderByFields.CreatedAt,
              direction: (sort?.direction || OrderByDirection.Desc).toLowerCase() as OrderDirection,
            }}
            onSortChange={newSort =>
              handleSortChange({
                field: newSort?.column as ProgrammingStructureOrderByFields,
                direction: newSort?.direction?.toUpperCase() as OrderByDirection,
              })
            }
            columns={[
              {
                isSortable: true,
                key: ProgrammingStructureOrderByFields.Code,
                header: t("programming-structures.table.headers.code"),
                renderCell: data => getColumnCell((data as ProgrammingStructure)?.code ?? ""),
              },
              {
                isSortable: true,
                key: ProgrammingStructureOrderByFields.Name,
                header: t("programming-structures.table.headers.name"),
                renderCell: data => getColumnCell((data as ProgrammingStructure)?.name ?? ""),
              },
              {
                isSortable: true,
                key: ProgrammingStructureOrderByFields.SubjectCodes,
                header: t("programming-structures.table.headers.subject"),
                renderCell: data =>
                  getColumnCell((data as ProgrammingStructure)?.subjectCodes?.join(", ") ?? ""),
              },
              {
                isSortable: true,
                key: ProgrammingStructureOrderByFields.Source,
                header: t("programming-structures.table.headers.type"),
                renderCell: data => {
                  const source = (data as ProgrammingStructure)?.source;
                  const labelKey = getStructureSourceLabelKey(source);

                  if (!labelKey) return getColumnCell(source ?? "");

                  return getPillCell(t(labelKey), {
                    color: "neutral-10",
                    textColor: "neutral-70",
                  });
                },
              },
              {
                isSortable: true,
                key: ProgrammingStructureOrderByFields.IsPublished,
                header: t("programming-structures.table.headers.status"),
                renderCell: data => {
                  const isPublished = !!(data as ProgrammingStructure)?.programmingStructureStats
                    ?.isPublished;

                  return getPillCell(
                    isPublished
                      ? t("programming-structures.table.body.published")
                      : t("programming-structures.table.body.not-published"),
                    {
                      color: isPublished ? "success-opacity" : "neutral-10",
                      textColor: isPublished ? "success-60" : "neutral-70",
                      icon: isPublished ? "check" : undefined,
                      iconColor: "success-60",
                    },
                  );
                },
              },
              {
                isSortable: true,
                key: ProgrammingStructureOrderByFields.IsVisible,
                header: t("programming-structures.table.headers.visibility"),
                renderCell: data => {
                  const isVisible = !!(data as ProgrammingStructure)?.programmingStructureStats
                    ?.isVisible;

                  return getPillCell(
                    isVisible
                      ? t("programming-structures.table.body.visible")
                      : t("programming-structures.table.body.not-visible"),
                    {
                      color: isVisible ? "success-opacity" : "neutral-10",
                      textColor: isVisible ? "success-60" : "neutral-70",
                    },
                  );
                },
              },
            ]}
            rightActions={[
              {
                renderContent: data => {
                  const structure = data as ProgrammingStructure;

                  return (
                    <ProgrammingStructuresMenu
                      structure={structure}
                      workspace={workspace}
                      scenario={scenario}
                      origin={origin}
                      onShowPublishModal={() => {
                        setSelectedStructure(structure);
                        setShowPublishModal(true);
                      }}
                      onShowDeleteModal={() => {
                        setSelectedStructure(structure);
                        setShowDeleteModal(true);
                      }}
                      onShowVisibilityModal={() => {
                        setSelectedStructure(structure);
                        setShowVisibilityModal(true);
                      }}
                    />
                  );
                },
              },
            ]}
          />

          {!hasResults && (
            <Text className={styles.programmingStructures_emptyResults}>
              {t("programming-structures.table.body.no-matches")}
            </Text>
          )}

          {hasResults && !!pageInfo?.page && (
            <Pager
              onChange={!loading ? handlePageChange : () => null}
              total={pageInfo?.total ?? 0}
              page={pageInfo?.page ?? 1}
              size={rowsPerPage}
              classname={{ global: styles.programmingStructures_pager }}
            />
          )}
        </section>
      )}

      {shouldShowPendingChangesWarning && (
        <CardNotification
          className={styles.programmingStructures_pendingChanges}
          state="warning"
          outlined
          title={t("programming-structures.notifications.cards.pending-changes.title")}
        />
      )}

      <PublishStructureModal
        isOpen={showPublishModal}
        programmingStructure={selectedStructure}
        onClose={() => setShowPublishModal(false)}
        onPublish={() => {
          setShowPublishModal(false);
          handleUpdateVisibilityOrPublish(selectedStructure?.id, true, true, "publish");
          setSelectedStructure(null);
        }}
      />

      <DeleteStructureModal
        isOpen={showDeleteModal}
        programmingStructure={selectedStructure}
        onClose={() => setShowDeleteModal(false)}
        onDelete={() => {
          setShowDeleteModal(false);
          handleDeleteProgrammingStructure(selectedStructure?.id);
          setSelectedStructure(null);
        }}
      />

      <VisibilityStructureModal
        isOpen={showVisibilityModal}
        programmingStructure={selectedStructure}
        onClose={() => setShowVisibilityModal(false)}
        onConfirm={() => {
          setShowVisibilityModal(false);
          handleUpdateVisibilityOrPublish(
            selectedStructure?.id,
            undefined,
            !selectedStructure?.programmingStructureStats?.isVisible,
            "visibility",
          );
          setSelectedStructure(null);
        }}
      />
    </ProgrammingStructuresLayout>
  );
};

export default ProgrammingStructures;
